import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, switchMap } from 'rxjs/operators';
import { LS_DISCUSSION } from '../../../core/constants/localStorage';
import { RoutingService } from '../../../core/services/routing.service';
import { LocalStorage } from '../../../core/storage/interfaces/local-storage.interface';
import { IDiscussion } from '../../../shared/models/discussion';
import { IDoc } from '../../../shared/models/doc';
import { IEditable } from '../../../shared/models/editable';
import { DocsFacade } from '../../../shared/stores/docs/docs.facade';
import { DiscussionService } from '../store/discussion.service';
import { DiscussionForm } from './discussion.new.form';

@Component({
  selector: 'app-new-discussion',
  templateUrl: './discussion.new.component.html',
  styleUrls: ['./discussion.new.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewDiscussionComponent implements OnInit {
  readonly form = new DiscussionForm();
  public discussion: IDiscussion;
  public editable: { docs: IDoc[] };

  discussionTitle = '';

  readonly docs = {
    stream: this.docsFacade.all,
    saved: new Set<IDoc>(),
    local: new Set<File>()
  };

  private readonly projectArticleTypeId = 10;

  constructor(
    private service: DiscussionService,
    private routing: RoutingService,
    private router: Router,
    private route: ActivatedRoute,
    private localStorage: LocalStorage,
    readonly docsFacade: DocsFacade
  ) {}

  ngOnInit() {
    this.initLocalStorage();
    this.route.url
      .pipe(
        filter(([_, edit]) => edit?.path === `edit`),
        switchMap((url) => this.service.getDiscussionById(+url[0].path))
      )
      .subscribe((discussion: IDiscussion) => {
        if (discussion.is_autor) {
          this.setForm(discussion);
        } else {
          this.router.navigate(['discussions', 'new']);
        }
      });

    this.discussionTitle = this.form.title.control.value;
  }

  get isPlainArticle() {
    return this.discussion?.category?.id !== this.projectArticleTypeId;
  }

  public async save() {
    const local = await this.docsFacade.add([...this.docs.local]);
    const docsIds = [...this.docs.saved, ...local].map(({ id }) => id);
    const data = this.form.getData(docsIds);

    this.service
      .saveArticle(data, this.discussion?.id)
      .subscribe((discussion) => {
        const { id, name } = discussion;
        const link = this.routing.getDiscussionLink(id, name);
        this.localStorage.removeItem(LS_DISCUSSION);
        this.router.navigate(link);
      });
  }

  protected setForm(discussion: IDiscussion) {
    this.discussion = discussion;
    this.form.patch({
      title: discussion.name,
      category: discussion.category,
      ageGroup: discussion.age_group,
      access: { id: discussion.access },
      text: discussion.description
    });

    if (!this.isPlainArticle) {
      this.form.adjustFormToPrivateArticle();
    }
  }

  protected initLocalStorage() {
    const cached = this.localStorage.getItem(LS_DISCUSSION);

    this.form.group.valueChanges.subscribe((value) => {
      const json = JSON.stringify(value);
      this.localStorage.setItem(LS_DISCUSSION, json);
    });

    if (!cached) return; // #Return#

    const value = JSON.parse(cached);
    this.form.patch(value);
  }
}
